import { UsageAlert } from "@/features/usage/components/UsageAlert";
import { useDailyMaps } from "@/features/usage/hooks/useLimit";
import { reportLimitError, userFacingLimitMessage } from "@/features/usage/limitErrors";
import { cn } from "@/utils/cn";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { executeCli } from "../api/playground";
import type { ConsoleEntry } from "../types";
import { stripAnsi } from "../utils/parseOutputs";

/** Imperative surface the agent box uses to "type" into the prompt. */
export interface ConsoleHandle {
  /** Clear the prompt and enter agent-typing mode. */
  beginAgentTyping: () => void;
  /** Queue a streamed chunk for the typewriter. */
  pushAgentText: (chunk: string) => void;
  /** Stream finished — drain the queue, then hand the prompt back. */
  endAgentTyping: () => void;
  /** Recent commands + result IDs, oldest first (context for the agent). */
  getHistory: () => string[];
}

const WELCOME = `Paperclip console — the same gateway the CLI uses.
Type a command and press Enter, or let the agent write one for you.
Try: search "CRISPR base editing" -n 5        (type \`clear\` to reset)`;

/** ~1 char per tick at 12ms reads as fast, deliberate typing. */
const TYPE_INTERVAL_MS = 12;

/** Strip markdown fences / prompts the model shouldn't emit but might. */
const sanitizeAgentChunk = (chunk: string) =>
  chunk.replace(/`/g, "").replace(/\r/g, "").replace(/\n+/g, " ");

/** Split a typed line into the {command, raw} shape /cli/execute expects. */
const splitCommandLine = (line: string): { command: string; raw: string } => {
  let trimmed = line.trim();
  if (trimmed.toLowerCase().startsWith("paperclip ")) {
    trimmed = trimmed.slice("paperclip ".length).trim();
  } else if (trimmed.toLowerCase() === "paperclip") {
    trimmed = "";
  }
  const space = trimmed.indexOf(" ");
  if (space === -1) return { command: trimmed, raw: "" };
  return { command: trimmed.slice(0, space), raw: trimmed.slice(space + 1).trim() };
};

/** Result IDs mentioned in output (s_…, m_…) — surfaced to the agent. */
const extractResultIds = (output: string): string[] =>
  Array.from(new Set(output.match(/\b[sm]_[a-zA-Z0-9]{4,}\b/g) ?? []));

export const ConsolePanel = forwardRef<
  ConsoleHandle,
  { onRequireAuth: () => boolean }
>(({ onRequireAuth }, ref) => {
  const [entries, setEntries] = useState<ConsoleEntry[]>([]);
  const [input, setInput] = useState("");
  const [agentTyping, setAgentTyping] = useState(false);
  const {
    isAtLimit: mapsAtLimit,
    isApproachingLimit: mapsApproaching,
    limit: mapsLimit,
    refetchUsage,
  } = useDailyMaps();

  const nextId = useRef(1);
  const historyRef = useRef<string[]>([]);
  const resultIdsRef = useRef<string[]>([]);
  const historyIndex = useRef<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Typewriter machinery: streamed chunks land in a queue; an interval
  // reveals them character by character so it reads like live typing.
  const queueRef = useRef("");
  const streamDoneRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const startTimer = useCallback(() => {
    if (timerRef.current) return;
    timerRef.current = setInterval(() => {
      if (queueRef.current.length > 0) {
        const ch = queueRef.current.slice(0, 1);
        queueRef.current = queueRef.current.slice(1);
        setInput((prev) => (prev + ch).replace(/\s{2,}/g, " "));
      } else if (streamDoneRef.current) {
        stopTimer();
        setAgentTyping(false);
        inputRef.current?.focus();
      }
    }, TYPE_INTERVAL_MS);
  }, []);

  useImperativeHandle(ref, () => ({
    beginAgentTyping: () => {
      queueRef.current = "";
      streamDoneRef.current = false;
      setInput("");
      setAgentTyping(true);
      startTimer();
    },
    pushAgentText: (chunk: string) => {
      queueRef.current += sanitizeAgentChunk(chunk);
      startTimer();
    },
    endAgentTyping: () => {
      streamDoneRef.current = true;
      startTimer();
    },
    getHistory: () => {
      const commands = historyRef.current.slice(-8).map((c) => `$ ${c}`);
      const results = resultIdsRef.current.slice(-4);
      return results.length > 0
        ? [...commands, `Recent result IDs: ${results.join(", ")}`]
        : commands;
    },
  }));

  useEffect(() => stopTimer, []);

  // Keep scrolled to the prompt as output arrives.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [entries, input, agentTyping]);

  const running = entries.some((e) => e.running);

  const runCommand = async (line: string) => {
    const trimmed = line.trim();
    if (!trimmed) return;
    if (trimmed === "clear") {
      setEntries([]);
      setInput("");
      return;
    }
    if (!onRequireAuth()) return;

    const { command, raw } = splitCommandLine(trimmed);
    if (!command) return;

    const isMapCommand = command === "map" || command === "verify";
    if (isMapCommand && mapsAtLimit) {
      const id = nextId.current++;
      historyRef.current.push(trimmed);
      historyIndex.current = null;
      setEntries((prev) => [
        ...prev,
        {
          id,
          command: trimmed,
          output: null,
          exitCode: 1,
          elapsedMs: 0,
          error: userFacingLimitMessage("playground", "quotaMaps"),
          running: false,
        },
      ]);
      setInput("");
      return;
    }

    historyRef.current.push(trimmed);
    historyIndex.current = null;

    const id = nextId.current++;
    setEntries((prev) => [
      ...prev,
      {
        id,
        command: trimmed,
        output: null,
        exitCode: null,
        elapsedMs: null,
        error: null,
        running: true,
      },
    ]);
    setInput("");

    const started = performance.now();
    try {
      const response = await executeCli(command, raw);
      if (isMapCommand) refetchUsage();
      resultIdsRef.current.push(...extractResultIds(response.output ?? ""));
      setEntries((prev) =>
        prev.map((e) =>
          e.id === id
            ? {
              ...e,
              running: false,
              output: response.output ?? "",
              exitCode: response.exit_code,
              elapsedMs: Math.round(performance.now() - started),
            }
            : e,
        ),
      );
    } catch (err) {
      const limitMessage = reportLimitError(err, "playground", {
        refetchUsage,
        quotaAlertVisible: mapsAtLimit,
      });
      setEntries((prev) =>
        prev.map((e) =>
          e.id === id
            ? {
              ...e,
              running: false,
              error: limitMessage ?? (err instanceof Error ? err.message : "Request failed"),
              elapsedMs: Math.round(performance.now() - started),
            }
            : e,
        ),
      );
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (agentTyping) {
      e.preventDefault();
      return;
    }
    if (e.key === "Enter" && !running) {
      e.preventDefault();
      void runCommand(input);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const h = historyRef.current;
      if (h.length === 0) return;
      const idx =
        historyIndex.current === null
          ? h.length - 1
          : Math.max(0, historyIndex.current - 1);
      historyIndex.current = idx;
      setInput(h[idx]);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      const h = historyRef.current;
      if (historyIndex.current === null) return;
      const idx = historyIndex.current + 1;
      if (idx >= h.length) {
        historyIndex.current = null;
        setInput("");
      } else {
        historyIndex.current = idx;
        setInput(h[idx]);
      }
    }
  };

  return (
    <section
      aria-label="Paperclip console"
      className="flex min-h-[520px] flex-col overflow-hidden rounded-[3px] border border-zinc-800 bg-zinc-950 "
    >
      {(mapsAtLimit || mapsApproaching) && (
        <div className="border-b border-zinc-800 px-3 py-2">
          <UsageAlert
            id="usage-alert-console-maps"
            name="dailyMaps"
            isAtLimit={mapsAtLimit}
            isApproachingLimit={mapsApproaching}
            limit={mapsLimit}
          />
        </div>
      )}
      <div className="flex items-center gap-2 border-b border-zinc-800 px-4 py-2.5">
        <span className="flex gap-1.5" aria-hidden>
          <span className="h-2.5 w-2.5 rounded-full bg-zinc-700" />
          <span className="h-2.5 w-2.5 rounded-full bg-zinc-700" />
          <span className="h-2.5 w-2.5 rounded-full bg-zinc-700" />
        </span>
        <span className="font-mono text-2xs text-zinc-500">
          paperclip console
        </span>
        {agentTyping && (
          <span className="ml-auto font-mono text-2xs text-emerald-400">
            agent is typing…
          </span>
        )}
      </div>

      {/* Click anywhere in the terminal to focus the prompt. */}
      <div
        ref={scrollRef}
        className="max-h-[65vh] flex-1 cursor-text overflow-y-auto px-4 py-3 font-mono text-xs leading-relaxed"
        onClick={(e) => {
          // Don't steal focus from text selection.
          if (window.getSelection()?.toString()) return;
          if (e.target === e.currentTarget || !agentTyping) {
            inputRef.current?.focus();
          }
        }}
      >
        <pre className="whitespace-pre-wrap text-zinc-500">{WELCOME}</pre>

        {entries.map((entry) => (
          <div key={entry.id} className="mt-3">
            <div className="flex gap-2">
              <span className="shrink-0 text-emerald-400">$</span>
              <span className="break-all text-zinc-100">{entry.command}</span>
            </div>
            {entry.running ? (
              <p className="mt-1 animate-pulse text-zinc-500">running…</p>
            ) : entry.error ? (
              <pre className="mt-1 whitespace-pre-wrap break-words text-red-400">
                {entry.error}
              </pre>
            ) : (
              <>
                {entry.output && (
                  <pre className="mt-1 whitespace-pre-wrap break-words text-zinc-300">
                    {stripAnsi(entry.output)}
                  </pre>
                )}
                <p className="mt-0.5 text-3xs text-zinc-600">
                  exit {entry.exitCode ?? 0}
                  {entry.elapsedMs != null && ` · ${(entry.elapsedMs / 1000).toFixed(1)}s`}
                </p>
              </>
            )}
          </div>
        ))}

        <div className="mt-3 flex items-center gap-2">
          <span className="shrink-0 text-emerald-400">$</span>
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => {
              if (!agentTyping) setInput(e.target.value);
            }}
            onKeyDown={onKeyDown}
            disabled={running}
            spellCheck={false}
            autoCapitalize="off"
            autoComplete="off"
            aria-label="Console command"
            aria-describedby={
              mapsAtLimit || mapsApproaching
                ? "usage-alert-console-maps"
                : undefined
            }
            placeholder={agentTyping ? "" : 'search "…" — Enter to run'}
            className={cn(
              "min-w-0 flex-1 border-0 bg-transparent p-0 font-mono text-xs text-zinc-100 caret-emerald-400 outline-none ring-0 placeholder:text-zinc-600 focus:outline-none focus:ring-0 disabled:opacity-60",
            )}
          />
          {agentTyping && (
            <span
              aria-hidden
              className="h-3.5 w-1.5 animate-pulse bg-emerald-400"
            />
          )}
        </div>
      </div>
    </section>
  );
});

ConsolePanel.displayName = "ConsolePanel";
